import { useState, useEffect, useCallback } from 'react';
import { useOrgTimezone, formatDateInTz } from '../../utils/timezone';
import { Link } from 'react-router-dom';
import {
  Plus, Shield, RefreshCw, AlertCircle, Unlock, Trash2,
  Radio, Activity, Droplet,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import PageHeader from '../../components/PageHeader';
import espnowService, { type EspNowController } from '../../services/espnowIrrigation';
import sensorDevicesService, { type SensorDevice } from '../../services/sensorDevices';
import XBeeSitePairing from '../../components/irrigation/XBeeSitePairing';
import PumpDeadmanScript from '../../components/irrigation/PumpDeadmanScript';
import { errorMessage } from '../../utils/apiResponse';
import { useAppRefresh } from '../../hooks/useAppRefresh';
import '../../styles/GlobalPageStyles.css';

/**
 * Unified device management. Users add a device by MAC only; the device
 * self-pairs on first boot using the password compiled into its firmware.
 * Standalone sensor devices (HTTP/MQTT transport) are listed read-only here
 * with unclaim/delete controls, but typical hardware is ESP-NOW based.
 */
export default function EspNowAdminSettings() {
  const [controllers, setControllers] = useState<EspNowController[]>([]);
  const [sensorDevices, setSensorDevices] = useState<SensorDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Add device form (MAC only)
  const [showAddForm, setShowAddForm] = useState(false);
  const [newControllerId, setNewControllerId] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ctrls, sensors] = await Promise.all([
        espnowService.adminGetAllControllers(),
        sensorDevicesService.listAllDevices().catch(() => [] as SensorDevice[]),
      ]);
      setControllers(ctrls);
      // Only show truly-standalone sensors here (not ESP-NOW peers of a listed controller,
      // which are managed inside the controller's detail page).
      const controllerIds = new Set(ctrls.map(c => c.controller_id));
      setSensorDevices(
        sensors.filter(s => !s.field_controller_id || !controllerIds.has(s.field_controller_id))
      );
    } catch {
      setError('Failed to load devices');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useAppRefresh(useCallback(() => { load(); }, [load]));

  async function handleAddDevice() {
    const id = newControllerId.trim().toUpperCase().replace(/:/g, '').replace(/\s/g, '');
    if (!id || id.length !== 12) {
      setError('Controller ID must be 12 hex characters (MAC address without colons)');
      return;
    }
    setAdding(true);
    setError(null);
    try {
      const created = await espnowService.adminAddController(id);
      setControllers(prev => [created, ...prev]);
      setNewControllerId('');
      setShowAddForm(false);
      setSuccess(`Device ${id} added. Power it on and it will auto-pair on first boot.`);
    } catch (err: unknown) {
      const msg = (err as { message?: string } | null)?.message;
      setError(msg || 'Failed to add device');
    } finally {
      setAdding(false);
    }
  }

  async function handleDeleteController(controllerId: string) {
    if (!confirm(`Permanently delete ${controllerId}? This removes all programs, events, routers, commands, and any sensors on this controller along with their recorded readings. This cannot be undone.`)) return;
    try {
      await espnowService.adminDeleteController(controllerId);
      setControllers(prev => prev.filter(c => c.controller_id !== controllerId));
      setSuccess(`${controllerId} deleted.`);
    } catch (err) {
      // The server's message is the useful part here: a delete can fail on a
      // foreign key it cannot resolve on its own (a program on ANOTHER site
      // pointing at this one's master pump, say), and "Failed to delete
      // controller" sends the operator to the logs to find that out.
      setError(errorMessage(err, 'Failed to delete controller'));
    }
  }

  async function handleUnclaimController(controllerId: string) {
    if (!confirm(`Reset pairing for ${controllerId}? The device will need to re-pair on next boot.`)) return;
    try {
      const updated = await espnowService.adminUnclaimController(controllerId);
      setControllers(prev => prev.map(c => c.controller_id === controllerId ? { ...c, ...updated } : c));
      setSuccess(`${controllerId} unclaimed — ready for re-pairing on next boot.`);
    } catch {
      setError('Failed to unclaim controller');
    }
  }

  async function handleDeleteSensor(deviceId: string) {
    if (!confirm(`Permanently delete sensor ${deviceId}? This removes all readings. Cannot be undone.`)) return;
    try {
      await sensorDevicesService.deleteDevice(deviceId);
      setSensorDevices(prev => prev.filter(d => d.device_id !== deviceId));
      setSuccess(`${deviceId} deleted.`);
    } catch {
      setError('Failed to delete sensor');
    }
  }

  async function handleUnclaimSensor(deviceId: string) {
    if (!confirm(`Reset pairing for sensor ${deviceId}? The device will need to re-pair on next boot.`)) return;
    try {
      await sensorDevicesService.unclaimDevice(deviceId);
      setSensorDevices(prev => prev.map(d => d.device_id === deviceId ? { ...d, api_key_hash: null, online: false } : d));
      setSuccess(`${deviceId} unclaimed.`);
    } catch {
      setError('Failed to unclaim sensor');
    }
  }

  return (
    <>
      <PageHeader
        title="Device Management"
        subtitle="Add, pair and manage controllers and sensors"
        icon={<Shield className="w-5 h-5 text-blue-600" />}
      />
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="space-y-6">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
              <button onClick={() => setError(null)} className="ml-auto text-red-400">×</button>
            </div>
          )}
          {success && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
              {success}
              <button onClick={() => setSuccess(null)} className="ml-auto text-green-400">×</button>
            </div>
          )}

          {/* XBee laptop sites. Kept above the MAC form because it is a different
              onboarding path entirely - a code, not a MAC - and putting it below
              invites people to try adding a 16-char coordinator address into a
              field that validates for 12. */}
          <XBeeSitePairing />

          {/* The pump dead-man script. Sits directly under XBee site pairing
              because it belongs to the same job - somebody is at the farm with a
              laptop commissioning hardware - and because it is XBee-only, so it
              would read as noise anywhere near the ESP-NOW controls below. */}
          <PumpDeadmanScript />

          {/* Add device — MAC only */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center justify-between">
                <span>Add a Device</span>
                <button
                  onClick={() => setShowAddForm(f => !f)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Add Device</span>
                  <span className="sm:hidden">Add</span>
                </button>
              </CardTitle>
            </CardHeader>
            {showAddForm && (
              <CardContent className="pt-0">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
                  <p className="text-sm text-blue-800">
                    Enter the <strong>MAC address</strong> (12 hex characters, e.g. <code>98A316DC22E0</code>).
                    It's usually printed on the device label.
                  </p>
                  <p className="text-sm text-blue-700">
                    After adding, power on the device. It connects to WiFi and <strong>self-pairs</strong>
                    using the password baked into its firmware — no further action required.
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <input
                      type="text"
                      placeholder="98A316DC22E0"
                      maxLength={17}
                      value={newControllerId}
                      onChange={e => setNewControllerId(e.target.value.toUpperCase())}
                      onKeyDown={e => { if (e.key === 'Enter') handleAddDevice(); }}
                      className="flex-1 min-w-0 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <Button onClick={handleAddDevice} disabled={adding} size="sm">
                      {adding ? 'Adding…' : 'Add'}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setShowAddForm(false); setNewControllerId(''); }}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Controllers */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Radio className="w-4 h-4 text-blue-500" />
                Controllers ({controllers.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-3">
              {loading ? (
                <div className="text-sm text-gray-400 flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin" /> Loading…
                </div>
              ) : controllers.length === 0 ? (
                <p className="text-sm text-gray-400 italic">No controllers added yet.</p>
              ) : (
                controllers.map(ctrl => (
                  <ControllerRow
                    key={ctrl.controller_id}
                    controller={ctrl}
                    onUnclaim={() => handleUnclaimController(ctrl.controller_id)}
                    onDelete={() => handleDeleteController(ctrl.controller_id)}
                  />
                ))
              )}
            </CardContent>
          </Card>

          {/* Standalone sensor devices */}
          {sensorDevices.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Activity className="w-4 h-4 text-violet-500" />
                  Standalone Sensors ({sensorDevices.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-2">
                <p className="text-xs text-gray-400 mb-2">
                  Sensors that report over HTTP or MQTT (not via an ESP-NOW controller).
                </p>
                {sensorDevices.map(d => (
                  <SensorRow
                    key={d.device_id}
                    device={d}
                    onUnclaim={() => handleUnclaimSensor(d.device_id)}
                    onDelete={() => handleDeleteSensor(d.device_id)}
                  />
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Controller Row ───────────────────────────────────────────────────────────

function ControllerRow({
  controller,
  onUnclaim,
  onDelete,
}: {
  controller: EspNowController;
  onUnclaim: () => void;
  onDelete: () => void;
}) {
  const timezone = useOrgTimezone();
  const isPaired = Boolean(controller.online || controller.last_seen);

  return (
    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200 flex-wrap">
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className={`w-2 h-2 rounded-full ${controller.online ? 'bg-green-500' : 'bg-gray-400'}`} />
        <Link
          to={`/irrigation/espnow/${controller.controller_id}`}
          className="font-mono text-sm font-medium text-gray-800 hover:text-blue-600"
        >
          {controller.controller_id}
        </Link>
        {controller.controller_name && (
          <span className="text-sm text-gray-500">— {controller.controller_name}</span>
        )}
      </div>

      <div className="flex items-center gap-1.5 text-xs text-gray-400 flex-wrap">
        {controller.organisation_name && (
          <span className="px-2 py-0.5 bg-blue-50 border border-blue-200 text-blue-700 rounded-full">
            {controller.organisation_name}
          </span>
        )}
        {controller.ip && <span>{controller.ip}</span>}
        {controller.last_seen && <span>Last seen {formatDateInTz(controller.last_seen, timezone)}</span>}
        {!isPaired && (
          <span className="px-2 py-0.5 bg-amber-50 border border-amber-200 text-amber-700 rounded-full">
            Not yet paired
          </span>
        )}
      </div>

      <div className="ml-auto flex items-center gap-1">
        <button
          onClick={onUnclaim}
          title="Reset pairing (clear API key — device will re-pair on next boot)"
          className="p-1.5 text-gray-400 hover:text-orange-500 hover:bg-orange-50 rounded-lg"
        >
          <Unlock className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={onDelete}
          title="Delete controller and all associated data"
          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ─── Sensor Row ───────────────────────────────────────────────────────────────

function SensorRow({
  device,
  onUnclaim,
  onDelete,
}: {
  device: SensorDevice;
  onUnclaim: () => void;
  onDelete: () => void;
}) {
  const timezone = useOrgTimezone();
  const isPaired = Boolean(device.api_key_hash) || Boolean(device.online) || Boolean(device.last_seen);

  return (
    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200 flex-wrap">
      <div className="flex items-center gap-2 flex-shrink-0">
        <Droplet className="w-3.5 h-3.5 text-violet-500" />
        <Link
          to={`/irrigation/sensors/${device.device_id}`}
          className="font-mono text-sm font-medium text-gray-800 hover:text-blue-600"
        >
          {device.device_id}
        </Link>
        {device.name && (
          <span className="text-sm text-gray-500">— {device.name}</span>
        )}
      </div>

      <div className="flex items-center gap-1.5 text-xs text-gray-400 flex-wrap">
        <span className="px-2 py-0.5 rounded-full bg-gray-200 text-gray-700">
          {device.type_display_name || device.type_key}
        </span>
        <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{device.transport}</span>
        {device.organisation_name && (
          <span className="px-2 py-0.5 bg-blue-50 border border-blue-200 text-blue-700 rounded-full">
            {device.organisation_name}
          </span>
        )}
        {device.last_seen && <span>Last seen {formatDateInTz(device.last_seen, timezone)}</span>}
        {!isPaired && (
          <span className="px-2 py-0.5 bg-amber-50 border border-amber-200 text-amber-700 rounded-full">
            Not yet paired
          </span>
        )}
      </div>

      <div className="ml-auto flex items-center gap-1">
        <button
          onClick={onUnclaim}
          title="Reset pairing"
          className="p-1.5 text-gray-400 hover:text-orange-500 hover:bg-orange-50 rounded-lg"
        >
          <Unlock className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={onDelete}
          title="Delete sensor and all readings"
          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

